import { HttpClient } from '@angular/common/http';
import { Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DataTableDirective } from 'angular-datatables';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { Generic, GenericCompartimentSearch } from 'src/app/interfaces/generic.interface';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { RapoarteActivitateService } from './rapoarte-activitate.service';
import { CommonService } from 'src/app/services/common.service';
import { AppFilesService } from 'src/app/services/app-files.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UserOrganigramaSearchPayload } from 'src/app/interfaces/common.interface';
import { formatDate } from '@angular/common';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import Swal from 'sweetalert2';
import { OperatorAccess } from 'src/app/platform/nomenclatoare/operatori/operatori.interface';
import { NotificationService } from 'src/app/services/notification.service';
import { Destinatie, Info, Origin, RaportData, SearchRapoartePayload } from './rapoarte-activitate.interface';
import { OperatoriService } from 'src/app/platform/nomenclatoare/operatori/operatori.service';

@Component({
    selector: 'app-rapoarte-activitate',
    templateUrl: './rapoarte-activitate.component.html',
    styleUrls: ['./rapoarte-activitate.component.scss']
})
export class RapoarteActivitateComponent {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;

    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;
    helper = helper;

    isLoading: boolean = false;
    today = new Date();
    userAccesses: OperatorAccess[] = [];
    activeAccess: OperatorAccess | null = null;

    registreActiveSwitch: Generic[] = [];

    rapoarteList: RaportData[] = [];

    defaultParameter: { name: string; id: number } = {
        name: '--Alegeți--',
        id: -1,
    };

    tipRapoarte: { name: string; id: number }[] = [
        { name: '(Registru) General/Global', id: 1 },
        { name: '(Registru) Tipuri de documente', id: 2 },
        { name: 'Destinatie servicii - general', id: 3 },
        { name: 'Destinatie servicii - tipuri documente', id: 4 },
    ]

    form: FormGroup = new FormGroup({
        origin_directie_id: new FormControl(null),
        origin_directie_name: new FormControl(''),
        origin_compartiment_id: new FormControl(null),
        origin_compartiment_name: new FormControl(''),
        origin_user_id: new FormControl({ value: -1, disabled: true }),
        origin_user_name: new FormControl(''),
        destinatie_directie_id: new FormControl(null),
        destinatie_directie_name: new FormControl(''),
        destinatie_compartiment_id: new FormControl(null),
        destinatie_compartiment_name: new FormControl(''),
        destinatie_user_id: new FormControl(-1),
        destinatie_user_name: new FormControl(''),
        document_number: new FormControl({ value: null, disabled: true }),
        date_input_start: new FormControl(''),
        date_input_end: new FormControl(''),
        id_serie: new FormControl(-1),
        registru_type: new FormControl(1),
    });

    private destroy$ = new Subject<void>();

    directiiOrigin: Generic[] = [];
    filteredDirectiiOrigin: Generic[] = [];
    directiiOriginControl = new FormControl();

    compartimenteOrigin: GenericCompartimentSearch[] = [];
    compartimenteOriginControl = new FormControl();
    isCompartimenteOriginLoading = false;
    showCompartimenteOriginMessage = true;
    compartimenteOriginLoadComplete = false;

    operatoriOrigin: Generic[] = [];
    filteredOperatoriOrigin: Generic[] = [];
    operatoriOriginControl = new FormControl();

    directiiDestinatie: Generic[] = [];
    filteredDirectiiDestinatie: Generic[] = [];
    directiiDestinatieControl = new FormControl();

    compartimenteDestinatie: GenericCompartimentSearch[] = [];
    compartimenteDestinatieControl = new FormControl();
    isCompartimenteDestinatieLoading = false;
    showCompartimenteDestinatieMessage = true;
    compartimenteDestinatieLoadComplete = false;

    operatoriDestinatie: Generic[] = [];
    filteredOperatoriDestinatie: Generic[] = [];
    operatoriDestinatieControl = new FormControl();

    constructor(
        private rapoarteActivitateService: RapoarteActivitateService,
        private commonService: CommonService,
        private operatoriService: OperatoriService,
        private notificationService: NotificationService,
        private appFilesService: AppFilesService,
        public permissionsService: PermissionsService,
    ) { }

    ngOnInit() {
        this.loadDirectiiActive();

        this.loadUserAccess();

        this.loadActiveSwitch();

        this.compartimenteOriginControl.valueChanges.pipe(
            debounceTime(600)
        ).subscribe(compartiment => {
            if (typeof compartiment === 'string') {
                this.fetchCompartimenteOrigin(compartiment);
                this.showCompartimenteOriginMessage = true;
                this.compartimenteOriginLoadComplete = false;
            }
        });

        this.compartimenteDestinatieControl.valueChanges.pipe(
            debounceTime(600)
        ).subscribe(compartiment => {
            if (typeof compartiment === 'string') {
                this.fetchCompartimenteDestinatie(compartiment);
                this.showCompartimenteDestinatieMessage = true;
                this.compartimenteDestinatieLoadComplete = false;
            }
        });

        this.renderFieldsSearch();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadUserAccess(): void {
        this.operatoriService.userAccess$
            .pipe(takeUntil(this.destroy$))
            .subscribe(userAccesses => {
                this.userAccesses = userAccesses;
                this.activeAccess = this.userAccesses.find(access => access.active) || null;

                if (this.activeAccess) {
                    // Set default directie, compartiment, user from activeAccess
                    this.directiiOriginControl.setValue({
                        id: this.activeAccess.id_directie,
                        name: this.activeAccess.denumire_directie,
                    });
                    this.form.controls['origin_directie_id'].patchValue(this.activeAccess.id_directie);
                    this.form.controls['origin_directie_name'].patchValue(this.activeAccess.denumire_directie);

                    this.compartimenteOriginControl.setValue({
                        id: this.activeAccess.id_compartiment,
                        name: this.activeAccess.denumire_compartiment,
                    });
                    this.form.controls['origin_compartiment_id'].patchValue(this.activeAccess.id_compartiment);
                    this.form.controls['origin_compartiment_name'].patchValue(this.activeAccess.denumire_compartiment);

                    this.loadOperatoriOrigin();
                    this.form.controls['origin_user_id'].patchValue(this.activeAccess.id_user);
                    this.form.controls['origin_user_name'].patchValue(this.activeAccess.name_user);
                }
            });
    }

    loadActiveSwitch() {
        this.isLoading = true;
        this.commonService
            .getActiveSwitch()
            .pipe(
                map((resp: Generic[]) => [...resp]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.registreActiveSwitch = results;
                },
                error: (error) => {
                    console.error('Error loading directii', error);
                },
            });
    }

    renderFieldsSearch() {
        this.directiiOriginControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            debounceTime(150),
            switchMap((inputValue) => {
                if (typeof inputValue !== 'string') {
                    return of(this.directiiOrigin);
                }
                if (inputValue.length < 1) {
                    this.filteredDirectiiOrigin = this.directiiOrigin;
                    return of(this.directiiOrigin);
                }
                return this.filterDirectiiOrigin(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectiiOrigin = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.operatoriOriginControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            debounceTime(150),
            switchMap((inputValue) => {
                if (typeof inputValue !== 'string') {
                    return of(this.operatoriOrigin);
                }
                if (inputValue.length < 1) {
                    this.filteredOperatoriOrigin = this.operatoriOrigin;
                    return of(this.operatoriOrigin);
                }
                return this.filterOperatoriOrigin(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredOperatoriOrigin = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.directiiDestinatieControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            debounceTime(150),
            switchMap((inputValue) => {
                if (typeof inputValue !== 'string') {
                    return of(this.directiiDestinatie);
                }
                if (inputValue.length < 1) {
                    this.filteredDirectiiDestinatie = this.directiiDestinatie;
                    return of(this.directiiDestinatie);
                }
                return this.filterDirectiiDestinatie(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectiiDestinatie = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.operatoriDestinatieControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            debounceTime(150),
            switchMap((inputValue) => {
                if (typeof inputValue !== 'string') {
                    return of(this.operatoriDestinatie);
                }
                if (inputValue.length < 1) {
                    this.filteredOperatoriDestinatie = this.operatoriDestinatie;
                    return of(this.operatoriDestinatie);
                }
                return this.filterOperatoriDestinatie(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredOperatoriDestinatie = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    loadDirectiiActive() {
        this.isLoading = true;
        this.commonService
            .getDirectiiActive()
            .pipe(
                map((results: any) => [...results]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.directiiOrigin = results;
                    this.directiiDestinatie = results;

                    this.filteredDirectiiOrigin = [...this.directiiOrigin];
                    this.filteredDirectiiDestinatie = [...this.directiiDestinatie];
                },
                error: (error) => {
                    console.error('Error loading directii', error);
                    this.filteredDirectiiOrigin = [];
                    this.filteredDirectiiDestinatie = [];
                },
            });
    }

    onDirectiiOriginChange(selectedCompartiment: Generic): void {
        this.form.controls['origin_directie_id'].patchValue(selectedCompartiment.id);
        this.form.controls['origin_directie_name'].patchValue(selectedCompartiment.name);

        this.compartimenteOriginControl.patchValue('');
        this.form.controls['origin_compartiment_id'].patchValue(null);
        this.form.controls['origin_compartiment_name'].patchValue('');

        this.operatoriOrigin = [];
        this.filteredOperatoriOrigin = [];
        this.operatoriOriginControl.patchValue('');
        this.form.controls['origin_user_id'].patchValue(-1);
        this.form.controls['origin_user_name'].patchValue('');
    }

    displayDirectiiOrigin(selectedDirectie: Generic): string {
        return selectedDirectie && selectedDirectie.name ? selectedDirectie.name : '';
    }

    onDirectiiOriginBlur() {
        const currentValue = this.directiiOriginControl.value;

        // If the value is a string (not an object), it means user didn't select from list
        if (typeof currentValue === 'string') {
            this.directiiOriginControl.patchValue('');
            this.form.controls['origin_directie_id'].patchValue(null);
            this.form.controls['origin_directie_name'].patchValue('');

            this.compartimenteOriginControl.patchValue('');
            this.form.controls['origin_compartiment_id'].patchValue(null);
            this.form.controls['origin_compartiment_name'].patchValue('');

            this.operatoriOrigin = [];
            this.filteredOperatoriOrigin = [];
            this.operatoriOriginControl.patchValue('');
            this.form.controls['origin_user_id'].patchValue(-1);
            this.form.controls['origin_user_name'].patchValue('');
        }
    }

    fetchCompartimenteOrigin(compartiment: string) {
        // const directieValue = this.form.get('origin_directie_id')?.value;
        // const directie = directieValue && directieValue !== -1 ? directieValue : undefined;
        if (compartiment.length >= 2) {
            this.isCompartimenteOriginLoading = true;
            this.commonService.searchCompartimenteActive(undefined, compartiment).subscribe({
                next: (response: any) => {
                    this.compartimenteOrigin = response;
                    this.isCompartimenteOriginLoading = false;
                    this.compartimenteOriginLoadComplete = true;
                    this.showCompartimenteOriginMessage = this.compartimenteOrigin.length === 0;
                },
                error: (err: any) => {
                    this.isCompartimenteOriginLoading = false;
                    this.showCompartimenteOriginMessage = true;
                    this.compartimenteOriginLoadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.compartimenteOrigin = [];
            this.showCompartimenteOriginMessage = true;
            this.compartimenteOriginLoadComplete = false;
        }
    }

    onCompartimenteOriginSelected(selectedCompartiment: GenericCompartimentSearch): void {
        this.form.controls['origin_compartiment_id'].patchValue(selectedCompartiment.id);
        this.form.controls['origin_compartiment_name'].patchValue(selectedCompartiment.name);

        this.directiiOriginControl.setValue({
            id: selectedCompartiment.directie_id,
            name: selectedCompartiment.directie_name,
        });
        this.form.controls['origin_directie_id'].patchValue(selectedCompartiment.directie_id);
        this.form.controls['origin_directie_name'].patchValue(selectedCompartiment.directie_name);

        this.loadOperatoriOrigin();
        this.operatoriOriginControl.patchValue('');
        this.form.controls['origin_user_id'].patchValue(-1);
        this.form.controls['origin_user_name'].patchValue('');
    }

    displayCompartimenteOrigin(selectedCompartiment: GenericCompartimentSearch): string {
        return selectedCompartiment && selectedCompartiment.name ? selectedCompartiment.name : '';
    }

    onCompartimenteOriginBlur() {
        const currentValue = this.compartimenteOriginControl.value;

        // If the value is a string (not an object), it means user didn't select from list
        if (typeof currentValue === 'string') {
            this.compartimenteOriginControl.patchValue('');
            this.form.controls['origin_compartiment_id'].patchValue(null);
            this.form.controls['origin_compartiment_name'].patchValue('');

            this.operatoriOrigin = [];
            this.filteredOperatoriOrigin = [];
            this.operatoriOriginControl.patchValue('');
            this.form.controls['origin_user_id'].patchValue(-1);
            this.form.controls['origin_user_name'].patchValue('');
        }
    }

    loadOperatoriOrigin() {
        this.isLoading = true;
        const payload: UserOrganigramaSearchPayload = {
            directie_id: this.form.controls['origin_directie_id'].value,
            compartiment_id: this.form.controls['origin_compartiment_id'].value
        }
        this.commonService
            .getUsersByOrganigrama(payload)
            .pipe(
                map((results: any) => [...results]),
                finalize(() => { this.isLoading = false })
            ).subscribe({
                next: (data) => {
                    this.operatoriOrigin = [...data];
                    this.filteredOperatoriOrigin = [...this.operatoriOrigin];

                    // If the active user is found in operatoriOrigin, update the form
                    const foundActiveUser = this.operatoriOrigin.find(user => user.id === this.activeAccess?.id_user)
                    if (foundActiveUser) {
                        this.form.controls['origin_user_id'].patchValue(this.activeAccess?.id_user);
                        this.form.controls['origin_user_name'].patchValue(this.activeAccess?.name_user);
                    }
                },
                error: (error) => {
                    console.error(error);
                    this.operatoriOrigin = [];
                    this.filteredOperatoriOrigin = [];
                },
            });
    }

    onOperatoriOriginChange(operatorId: number): void {
        const selectedOperator = this.operatoriOrigin.find(operator => operator.id === operatorId);

        if (selectedOperator) {
            // this.form.controls['origin_user_id'].patchValue(selectedOperator.id);
            this.form.controls['origin_user_name'].patchValue(selectedOperator.name);
        }
    }

    onDirectiiDestinatieChange(selectedCompartiment: Generic): void {
        this.form.controls['destinatie_directie_id'].patchValue(selectedCompartiment.id);
        this.form.controls['destinatie_directie_name'].patchValue(selectedCompartiment.name);

        this.compartimenteDestinatieControl.patchValue('');
        this.form.controls['destinatie_compartiment_id'].patchValue(null);
        this.form.controls['destinatie_compartiment_name'].patchValue('');

        this.operatoriDestinatie = [];
        this.filteredOperatoriDestinatie = [];
        this.operatoriDestinatieControl.patchValue('');
        this.form.controls['destinatie_user_id'].patchValue(-1);
        this.form.controls['destinatie_user_name'].patchValue('');
    }

    displayDirectiiDestinatie(selectedDirectie: Generic): string {
        return selectedDirectie && selectedDirectie.name ? selectedDirectie.name : '';
    }

    onDirectiiDestinatieBlur() {
        const currentValue = this.directiiDestinatieControl.value;

        // If the value is a string (not an object), it means user didn't select from list
        if (typeof currentValue === 'string') {
            this.directiiDestinatieControl.patchValue('');
            this.form.controls['destinatie_directie_id'].patchValue(null);
            this.form.controls['destinatie_directie_name'].patchValue('');

            this.compartimenteDestinatieControl.patchValue('');
            this.form.controls['destinatie_compartiment_id'].patchValue(null);
            this.form.controls['destinatie_compartiment_name'].patchValue('');

            this.operatoriDestinatie = [];
            this.filteredOperatoriDestinatie = [];
            this.operatoriDestinatieControl.patchValue('');
            this.form.controls['destinatie_user_id'].patchValue(-1);
            this.form.controls['destinatie_user_name'].patchValue('');
        }
    }

    fetchCompartimenteDestinatie(compartiment: string) {
        // const directieValue = this.form.get('destinatie_directie_id')?.value;
        // const directie = directieValue && directieValue !== -1 ? directieValue : undefined;
        if (compartiment.length >= 2) {
            this.isCompartimenteDestinatieLoading = true;
            this.commonService.searchCompartimenteActive(undefined, compartiment).subscribe({
                next: (response: any) => {
                    this.compartimenteDestinatie = response;
                    this.isCompartimenteDestinatieLoading = false;
                    this.compartimenteDestinatieLoadComplete = true;
                    this.showCompartimenteDestinatieMessage = this.compartimenteDestinatie.length === 0;
                },
                error: (err: any) => {
                    this.isCompartimenteDestinatieLoading = false;
                    this.showCompartimenteDestinatieMessage = true;
                    this.compartimenteDestinatieLoadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.compartimenteDestinatie = [];
            this.showCompartimenteDestinatieMessage = true;
            this.compartimenteDestinatieLoadComplete = false;
        }
    }

    onCompartimenteDestinatieSelected(selectedCompartiment: GenericCompartimentSearch): void {
        this.form.controls['destinatie_compartiment_id'].patchValue(selectedCompartiment.id);
        this.form.controls['destinatie_compartiment_name'].patchValue(selectedCompartiment.name);

        this.directiiDestinatieControl.setValue({
            id: selectedCompartiment.directie_id,
            name: selectedCompartiment.directie_name,
        });
        this.form.controls['destinatie_directie_id'].patchValue(selectedCompartiment.directie_id);
        this.form.controls['destinatie_directie_name'].patchValue(selectedCompartiment.directie_name);

        this.loadOperatoriDestinatie();
        this.operatoriDestinatieControl.patchValue('');
        this.form.controls['destinatie_user_id'].patchValue(-1);
        this.form.controls['destinatie_user_name'].patchValue('');
    }

    displayCompartimenteDestinatie(selectedCompartiment: GenericCompartimentSearch): string {
        return selectedCompartiment && selectedCompartiment.name ? selectedCompartiment.name : '';
    }

    onCompartimenteDestinatieBlur() {
        const currentValue = this.compartimenteDestinatieControl.value;

        // If the value is a string (not an object), it means user didn't select from list
        if (typeof currentValue === 'string') {
            this.compartimenteDestinatieControl.patchValue('');
            this.form.controls['destinatie_compartiment_id'].patchValue(null);
            this.form.controls['destinatie_compartiment_name'].patchValue('');

            this.operatoriDestinatie = [];
            this.filteredOperatoriDestinatie = [];
            this.operatoriDestinatieControl.patchValue('');
            this.form.controls['destinatie_user_id'].patchValue(-1);
            this.form.controls['destinatie_user_name'].patchValue('');
        }
    }

    loadOperatoriDestinatie() {
        this.isLoading = true;
        const payload: UserOrganigramaSearchPayload = {
            directie_id: this.form.controls['destinatie_directie_id'].value,
            compartiment_id: this.form.controls['destinatie_compartiment_id'].value
        }
        this.commonService
            .getUsersByOrganigrama(payload)
            .pipe(
                map((results: any) => [...results]),
                finalize(() => { this.isLoading = false })
            ).subscribe({
                next: (data) => {
                    this.operatoriDestinatie = [...data];
                    this.filteredOperatoriDestinatie = [...this.operatoriDestinatie];
                },
                error: (error) => {
                    console.error(error);
                    this.operatoriDestinatie = [];
                    this.filteredOperatoriDestinatie = [];
                },
            });
    }

    onOperatoriDestinatieChange(operatorId: number): void {
        const selectedOperator = this.operatoriDestinatie.find(operator => operator.id === operatorId);

        if (selectedOperator) {
            // this.form.controls['destinatie_user_id'].patchValue(selectedOperator.id);
            this.form.controls['destinatie_user_name'].patchValue(selectedOperator.name);
        }
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectiiOrigin(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiOrigin.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    filterOperatoriOrigin(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.operatoriOrigin.filter(operator =>
            this.normalizeString(operator.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    filterDirectiiDestinatie(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiDestinatie.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    filterOperatoriDestinatie(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.operatoriDestinatie.filter(operator =>
            this.normalizeString(operator.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    generatePayload() {
        const date_input_start = this.form.value.date_input_start;
        const date_input_end = this.form.value.date_input_end;

        const origin: Origin = {
            directie_id: this.form.get('origin_directie_id')?.value,
            directie_name: this.form.get('origin_directie_name')?.value,
            compartiment_id: this.form.get('origin_compartiment_id')?.value,
            compartiment_name: this.form.get('origin_compartiment_name')?.value,
            user_id: this.form.get('origin_user_id')?.value,
            user_name: this.form.get('origin_user_name')?.value,
        };

        const destinatie: Destinatie = {
            directie_id: this.form.get('destinatie_directie_id')?.value,
            directie_name: this.form.get('destinatie_directie_name')?.value,
            compartiment_id: this.form.get('destinatie_compartiment_id')?.value,
            compartiment_name: this.form.get('destinatie_compartiment_name')?.value,
            user_id: this.form.get('destinatie_user_id')?.value,
            user_name: this.form.get('destinatie_user_name')?.value,
        };

        const info: Info = {
            number: this.form.get('document_number')?.value,
            date_input_start: this.form.get('date_input_start')?.value,
            date_start: date_input_start ? formatDate(date_input_start, 'yyyy-MM-dd', 'en-US') : '',
            date_input_end: this.form.get('date_input_end')?.value,
            date_end: date_input_end ? formatDate(date_input_end, 'yyyy-MM-dd', 'en-US') : '',
            id_serie: this.form.get('id_serie')?.value,
            registru_type: this.form.get('registru_type')?.value,
        };

        const payload: SearchRapoartePayload = { origin: origin, destinatie: destinatie, info: info };

        return payload;
    }

    searchRapoarte() {
        this.isLoading = true;

        const payload: SearchRapoartePayload = this.generatePayload();

        this.rapoarteActivitateService
            .getRapoarte(payload)
            .pipe(
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (results) => {
                    this.rapoarteList = results;
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    resetForm() {
        this.form.patchValue({
            destinatie_directie_id: null,
            destinatie_directie_name: '',
            destinatie_compartiment_id: null,
            destinatie_compartiment_name: '',
            destinatie_user_id: -1,
            destinatie_user_name: '',
            document_number: null,
            date_input_start: '',
            date_input_end: '',
            id_serie: -1,
            registru_type: 1,
        });

        this.directiiDestinatieControl.setValue('');

        this.compartimenteDestinatie = [];
        this.compartimenteDestinatieControl.setValue('');

        this.operatoriDestinatie = [];
        this.filteredOperatoriDestinatie = [];
        this.operatoriDestinatieControl.setValue('');
    }

    exportFile(type: number) {
        this.isLoading = true;
        let fullPath = this.rapoarteActivitateService.exportFileUrl + `?download=${type}`;

        const payload: SearchRapoartePayload = this.generatePayload();

        this.appFilesService.postDownloadFile(fullPath, payload)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('Export error: ', err);
                this.isLoading = false;
            })
    }
}
